import api from './client'
import type { Project, ProjectMember, IssueStatus, Label, Component, Milestone } from '../types'

export const projectsApi = {
  // Projects
  listProjects: () => api.get<{ data: Project[] }>('/projects'),
  getProject: (id: string) => api.get<{ data: Project }>(`/projects/${id}`),
  createProject: (data: Partial<Project>) => api.post<{ data: Project }>('/projects', data),
  updateProject: (id: string, data: Partial<Project>) => api.put<{ data: Project }>(`/projects/${id}`, data),
  archiveProject: (id: string) => api.post<{ data: Project }>(`/projects/${id}/archive`),
  deleteProject: (id: string) => api.delete(`/projects/${id}`),

  // Members
  listMembers: (projectId: string) =>
    api.get<{ data: ProjectMember[] }>(`/projects/${projectId}/members`),
  addMember: (projectId: string, userId: string) =>
    api.post<{ data: ProjectMember }>(`/projects/${projectId}/members`, { userId }),
  removeMember: (projectId: string, userIdOrMemberId: string) =>
    api.delete(`/projects/${projectId}/members/${userIdOrMemberId}`),

  // Statuses
  listStatuses: (projectId: string) =>
    api.get<{ data: IssueStatus[] }>(`/projects/${projectId}/statuses`),
  createStatus: (projectId: string, data: Partial<IssueStatus>) =>
    api.post<{ data: IssueStatus }>(`/projects/${projectId}/statuses`, data),
  updateStatus: (projectId: string, statusId: string, data: Partial<IssueStatus>) =>
    api.put<{ data: IssueStatus }>(`/projects/${projectId}/statuses/${statusId}`, data),
  deleteStatus: (projectId: string, statusId: string) =>
    api.delete(`/projects/${projectId}/statuses/${statusId}`),
  reorderStatuses: (projectId: string, orderedIds: string[]) =>
    api.put(`/projects/${projectId}/statuses/reorder`, { orderedIds }),

  // Labels
  listLabels: (projectId: string) =>
    api.get<{ data: Label[] }>(`/projects/${projectId}/labels`),
  createLabel: (projectId: string, data: Partial<Label>) =>
    api.post<{ data: Label }>(`/projects/${projectId}/labels`, data),
  updateLabel: (projectId: string, labelId: string, data: Partial<Label>) =>
    api.put<{ data: Label }>(`/projects/${projectId}/labels/${labelId}`, data),
  deleteLabel: (projectId: string, labelId: string) =>
    api.delete(`/projects/${projectId}/labels/${labelId}`),

  // Components
  listComponents: (projectId: string) =>
    api.get<{ data: Component[] }>(`/projects/${projectId}/components`),
  createComponent: (projectId: string, data: Partial<Component>) =>
    api.post<{ data: Component }>(`/projects/${projectId}/components`, data),
  updateComponent: (projectId: string, componentId: string, data: Partial<Component>) =>
    api.put<{ data: Component }>(`/projects/${projectId}/components/${componentId}`, data),
  deleteComponent: (projectId: string, componentId: string) =>
    api.delete(`/projects/${projectId}/components/${componentId}`),

  // Milestones
  listMilestones: (projectId: string) =>
    api.get<{ data: Milestone[] }>(`/projects/${projectId}/milestones`),
  createMilestone: (projectId: string, data: Partial<Milestone>) =>
    api.post<{ data: Milestone }>(`/projects/${projectId}/milestones`, data),
  updateMilestone: (projectId: string, milestoneId: string, data: Partial<Milestone>) =>
    api.put<{ data: Milestone }>(`/projects/${projectId}/milestones/${milestoneId}`, data),
  deleteMilestone: (projectId: string, milestoneId: string) =>
    api.delete(`/projects/${projectId}/milestones/${milestoneId}`),

  // Issue sheet import/export
  exportIssuesSheet: (projectId: string) =>
    api.get<Blob>(`/projects/${projectId}/issues/export`, { responseType: 'blob' }),
  importIssuesSheet: (projectId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post<{ data: { created: number; updated: number; warnings: string[] } }>(
      `/projects/${projectId}/issues/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  },
}
